import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { mdiCheckCircleOutline, mdiInformationOutline } from "@mdi/js";
import Icon from "@mdi/react";
import { useToggle } from "usehooks-ts";

import Button from "components/Button";
import ConfirmationModal from "components/ConfirmationModal";
import Header from "components/Header";
import Modal from "components/Modal";
import { RESPONSE_STATUS } from "constants/status";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import MainLayout from "layouts/Main";
import { IBlockUser } from "models/UserSetting";
import { resetPasswordAPI } from "services/api/user";
import { unblockUserAPI } from "services/api/userSetting";
import { AppDispatch, RootState } from "store";
import { handleLoading } from "store/global/loader";
import { getBlockUsers } from "store/userSetting/actions";
import UpdateEmail from "./UpdateEmail";
import styles from "./style.module.scss";

interface IAccountForm {
  email?: string;
  confirmEmail?: string;
}

export default function UserAccount() {
  const dispatch = useDispatch<AppDispatch>();
  const [isUnblockConfirmModalOpen, toggleUnblockConfirmModal] = useToggle();
  const [isSuccessModalOpen, toggleSuccessModal] = useToggle();
  const [blockedUserId, setBlockedUserId] = useState("");

  const {
    account: { email }
  } = useSelector((state: RootState) => state.authState);
  const { blockUsers }: { blockUsers: IBlockUser[] } = useSelector(
    (state: RootState) => state.userSettingState
  );

  useEffect(() => {
    const fetchBlockedUserList = async () => {
      dispatch(getBlockUsers());
    };

    fetchBlockedUserList();
  }, []);

  const {
    formState: { isSubmitting },
    handleSubmit
  } = useForm<IAccountForm>({
    values: { email }
  });

  const onSubmit = async ({ email }: IAccountForm) => {
    dispatch(handleLoading(true));

    const {
      data: { status, message }
    } = await resetPasswordAPI(email).finally(() => dispatch(handleLoading(false)));

    if (status === RESPONSE_STATUS.SUCCESS) {
      toggleSuccessModal();
    } else {
      showErrorMessage(message);
    }
  };

  const handleBlockUser = (id: string) => {
    setBlockedUserId(id);
    toggleUnblockConfirmModal();
  };

  const unblockUser = async () => {
    try {
      dispatch(handleLoading(true));
      await unblockUserAPI(blockedUserId);
      await dispatch(getBlockUsers());
      showSuccessMessage("Successfully unblock user");
    } catch {
      showErrorMessage("Error unblock user");
    } finally {
      dispatch(handleLoading(false));
    }
    setBlockedUserId("");
    toggleUnblockConfirmModal();
  };

  return (
    <MainLayout
      desktopTitle="Account"
      customHeader={
        <Header label="Account" headerIcon={<Icon path={mdiInformationOutline} />} isHideDesktop />
      }
      hideNavigation
      className={styles.userAccountContainer}>
      <div className={styles.formContainer}>
        <UpdateEmail />
        <form className={styles.updatePasswordContainer} onSubmit={handleSubmit(onSubmit)}>
          <span>Reset Password</span>
          <Button
            type="submit"
            size="lg"
            className="flex-none desktop:!self-end desktop:!px-10 desktop:!w-fit desktop:!h-12"
            loading={isSubmitting}>
            Reset Password
          </Button>
        </form>
        <Modal
          isOpen={isSuccessModalOpen}
          closeModal={toggleSuccessModal}
          wrapperClass="!m-auto text-sm desktop:!min-h-fit desktop:!min-w-fit"
          dialogClass="!m-auto !bg-foreground !p-4 !border !border-card !overflow-y-auto gap-4"
          closeClass="!top-24 !right-6 !bg-transparent !text-placeholder">
          <div className="flex gap-1 text-xl font-bold text-success">
            <Icon path={mdiCheckCircleOutline} />
            <span>Success</span>
          </div>
          <p className="px-6">We sent you an email with the password reset link. Thank you.</p>
          <Button onClick={toggleSuccessModal} className="self-end w-20 rounded-full h-9">
            OK
          </Button>
        </Modal>
      </div>
      <span className={styles.blockedUsersTitle}>Blocked Users</span>

      <ul className={styles.blockedUsersBody}>
        {blockUsers?.map((item) => (
          <li key={item.id} className={styles.list}>
            <span className="capitalize">{item.to_user_dba}</span>
            <Button
              variant="outline"
              rounded
              className="h-10"
              onClick={() => handleBlockUser(item.id)}>
              Unblock
            </Button>
          </li>
        ))}
      </ul>

      <ConfirmationModal
        isConfirmModalOpen={isUnblockConfirmModalOpen}
        toggleConfirmModal={toggleUnblockConfirmModal}
        content="Are you sure you’d like to unblock this user and all of their posts?"
        onOK={unblockUser}
      />
    </MainLayout>
  );
}
